import {
  Project,
  Survey,
  SurveyState
} from '../../../data/gql-gen/dashboard/graphql'

/**
 * Surveys in stable creation order (oldest first). The `surveys` field is not
 * returned in a guaranteed order, so we sort by `createdDate`. The auto-created
 * master survey is the oldest, so it is always `surveys[0]`. Shared by a
 * project's markets and a tracker wave's own markets.
 */
export const surveysByCreation = (surveys?: Survey[]): Survey[] =>
  [...(surveys ?? [])].sort(
    (a, b) =>
      new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
  )

/**
 * The markets of a multi-market project, in creation order (newest last). The
 * auto-created master is a real market that respondents take, so it is included
 * and comes first. Callers that need the shared questionnaire template
 * specifically use getMasterSurvey instead.
 *
 * Creation order is NOT base-market order: the base is the market flagged
 * isBase. surveys[0] is simply the oldest; a project may have no base at all
 * until one is picked, and a market can only be removed while in Draft.
 */
export const getMarkets = (project?: Project): Survey[] =>
  surveysByCreation(project?.surveys)

/**
 * The master survey (the auto-created template), i.e. the oldest survey. This is
 * the questionnaire's scope, not the project's base market — see getMarkets.
 */
export const getMasterSurvey = (project?: Project): Survey | undefined =>
  surveysByCreation(project?.surveys)[0]

/**
 * Whether any market (the base/master included) has left Draft. Once true, the
 * shared master questionnaire is locked project-wide: the Survey step leaves
 * the flow and the Mapping edit links hide. Per-market audience/fieldwork
 * editability is NOT keyed off this — each market locks on its own state.
 */
export const isProjectInProgress = (project?: Project): boolean =>
  getMarkets(project).some((market) => market.state !== SurveyState.Draft)
